import { forwardRef } from 'react';
import type { HTMLAttributes } from 'react';
import { cx } from '../utils/cx.js';
import styles from './Heading.module.css';

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
export type HeadingSize = 'base' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'hero';
export type HeadingWeight = 'medium' | 'semibold' | 'bold';
export type HeadingColor = 'text' | 'secondary' | 'accent' | 'warm';

/** The screen-title scale each heading level uses unless `size` overrides it,
 *  taken from how the mockups actually set panel titles vs. stage headings. */
const defaultSizeForLevel: Record<HeadingLevel, HeadingSize> = {
  1: '3xl',
  2: '2xl',
  3: 'xl',
  4: 'lg',
  5: 'md',
  6: 'base',
};

const sizeClass: Record<HeadingSize, string | undefined> = {
  base: styles.sizeBase,
  md: styles.sizeMd,
  lg: styles.sizeLg,
  xl: styles.sizeXl,
  '2xl': styles.size2xl,
  '3xl': styles.size3xl,
  hero: styles.sizeHero,
};

const weightClass: Record<HeadingWeight, string | undefined> = {
  medium: undefined,
  semibold: styles.weightSemibold,
  bold: styles.weightBold,
};

const colorClass: Record<HeadingColor, string | undefined> = {
  text: styles.colorText,
  secondary: styles.colorSecondary,
  accent: styles.colorAccent,
  warm: styles.colorWarm,
};

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  /** Which heading tag to render (h1–h6) — kept separate from `size` so
   *  document structure and visual scale can vary independently. */
  level?: HeadingLevel;
  size?: HeadingSize;
  weight?: HeadingWeight;
  color?: HeadingColor;
}

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(function Heading(
  { level = 2, size, weight = 'medium', color = 'text', className, ...rest },
  ref,
) {
  const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  const resolvedSize = size ?? defaultSizeForLevel[level];
  return (
    <Tag
      ref={ref}
      className={cx(
        styles.heading,
        sizeClass[resolvedSize],
        weightClass[weight],
        colorClass[color],
        className,
      )}
      {...rest}
    />
  );
});
